import { Inject, Injectable, DOCUMENT } from '@angular/core'
import { filter, tap } from 'rxjs/operators'
import { UserPermissions, UserPermissionsService } from '@core/user-permissions'
import { CampaignType } from '@app/models/campaign-type'
import { ViewType } from '@app/models/view-type'
import { WINDOW } from '@app/services/window.service'

import { SiteSettingsService } from '@app/services/site-settings.service'
import { ActivationEnd, Router, UrlSerializer } from '@angular/router'
import { combineLatest, Observable } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class ReturnUrlService {
  private user: UserPermissions
  private routeEvent: ActivationEnd
  private navEl: HTMLElement | null
  private navTimer: any
  private navRetries = 0
  private interval = 200
  private maxRetries = 50
  private linkTimer: any
  private linkRetries = 0

  constructor(
    private userPermissionsService: UserPermissionsService,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private siteSettingsService: SiteSettingsService,
    private router: Router,
    private urlSerializer: UrlSerializer
  ) {}

  public run(): Observable<any> {
    return combineLatest([this.userPermissionsService.getCurrentValue(), this.router.events]).pipe(
      filter(([user, event]) => !!user && event instanceof ActivationEnd && event.snapshot.children.length === 0),
      tap(([user, routeEvent]) => {
        this.resetNavTimer()
        this.user = user
        this.routeEvent = routeEvent
        this.init()
      })
    )
  }

  private init() {
    const navEl = document.getElementById('navAccount')
    if (navEl) {
      this.navEl = navEl
      if (this.user.isLoggedIn) {
        this.loggedInUserInit()
      } else {
        this.loggedOutUserInit()
      }
    } else if (this.navRetries++ < this.maxRetries) {
      this.navTimer = setTimeout(this.init.bind(this), this.interval)
    }
  }

  private loggedInUserInit(): void {
    this.navEl.addEventListener('click', this.loggedInUserClick)
  }

  private loggedInUserClick = (): void => {
    const linkEl = this.document.getElementById('navSignOutLink')?.childNodes?.[0]

    if (linkEl) {
      linkEl['href'] = this.buildUrl('signout')
      this.resetLinkTimer()
    } else if (this.linkRetries++ < this.maxRetries) {
      this.linkTimer = setTimeout(this.loggedInUserClick.bind(this), this.interval)
    } else {
      this.resetLinkTimer()
    }
  }

  private loggedOutUserInit(): void {
    const isHoliday = this.user.campaign.type === CampaignType.holiday
    const isStoryPage = this.routeEvent.snapshot.data.viewType === ViewType.connections
    const isPublic = this.user.campaign.type === CampaignType.public
    let returnUrl: string
    let queryParams: { [key: string]: string }

    if (isHoliday && isStoryPage) {
      returnUrl = '/stories'
    }

    if (isStoryPage && isPublic) {
      queryParams = {
        attachstories: '1',
      }
    }

    this.navEl['href'] = this.buildUrl('signin', returnUrl, queryParams)
  }

  private buildUrl(cta: 'signin' | 'signout', returnUrl?: string, queryParams?: { [key: string]: string }): string {
    const urlTree = this.urlSerializer.parse(returnUrl || this.router.url)
    Object.assign(urlTree.queryParams, queryParams)
    const siteUrl = this.siteSettingsService.siteUrl
    const origin = this.window.location.origin
    const url = origin + decodeURIComponent(this.urlSerializer.serialize(urlTree))

    return `${siteUrl}/account/${cta}?returnUrl=${encodeURIComponent(url)}`
  }

  private resetNavTimer() {
    clearTimeout(this.navTimer)
    this.navRetries = 0
    this.resetLinkTimer()
  }

  private resetLinkTimer() {
    clearTimeout(this.linkTimer)
    this.linkRetries = 0
  }
}
